'use client';

/**
 * CostPlanGraphReagraph — same ReaGraph shell as ProjectGraphReagraph,
 * rooted at a CostPlan instead of a Project.
 *
 * Deliberately a sibling copy, not a shared abstraction yet — the
 * transform/path-finding/zoom/context-menu/legend logic below is
 * 100% generic over `{nodes, edges}` and identical between the two;
 * the only real difference is which hook fetches the data
 * (`useMcpGraph` → `mcp_graph` strategy vs `useProjectGraph` →
 * `graph_explore.project_visualize`) and the header/empty-state copy.
 * Worth extracting into one presentational component + two thin
 * data-fetching wrappers if a third entity-rooted graph shows up;
 * not worth the abstraction for two.
 *
 * @see klyde_edge/klyde_docs/businessrules/costplan_graph.py — mcp_graph's shaper
 * @see klyde_ipe/components/canvas/cards/obeya/project-graph-reagraph.tsx
 */

import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Loader2,
  AlertCircle,
  Network,
  ZoomIn,
  ZoomOut,
  Maximize2,
  Route,
  X,
  MousePointer2,
  ExternalLink,
  Copy,
  Eye,
} from 'lucide-react';
import { useTheme } from 'next-themes';
import { useMcpGraph } from '@/lib/api/hooks';
import { useCapabilityStore } from '@/lib/stores/capability-store';
import { useGraphSelectionStore } from '@/lib/stores/graph-selection-store';
import { useCardExport } from '@/lib/design/card-export';
import { useCardIdentity } from '@/lib/design/card-chrome';
import type { GraphNode as APIGraphNode, GraphEdge as APIGraphEdge } from '@/lib/api/types';

interface CostPlanGraphReagraphProps {
  costPlanId: string;
  costPlanName?: string;
}

// Node colors by type — same palette as ProjectGraphReagraph plus
// `room`, the one type unique to a CostPlan graph (synthesized from
// room_placements, see costplan_graph.py's module docstring).
const NODE_COLORS: Record<string, string> = {
  project: '#4A90D9',
  rfq: '#F5A623',
  po: '#7ED321',
  vendor: '#BD10E0',
  lineitem: '#F8E71C',
  product: '#4DD0E1',
  mcp: '#F59E0B',
  room: '#26A69A',
  vendor_response: '#E91E63',
};

const NODE_SIZES: Record<string, number> = {
  project: 14,
  rfq: 10,
  po: 10,
  vendor: 12,
  lineitem: 6,
  product: 6,
  mcp: 12,
  room: 6,
  vendor_response: 8,
};

const EDGE_COLORS: Record<string, string> = {
  FOR_PROJECT: '#4A90D9',
  HAS_LINE: '#9B9B9B',
  FOR_PRODUCT: '#4DD0E1',
  LINKED_TO: '#F5A623',
  vendor: '#BD10E0',
  room: '#26A69A',
  quoted: '#E91E63',
  default: '#666666',
};

// Node types obeya mode has a real selection binding for (the same
// vendor_response -> rfi_details wiring the RFI matrix already uses,
// per total_matrix.py's obeya ModeConfig) — the only ones safe to push
// into the shared graph-selection-store from a click. `lineitem` and
// `room` are synthesized/non-addressable (no CostPlan-line or room
// detail view exists yet); clicking them stays a local highlight only.
// See handleNodeClick's comment.
const SELECTABLE_NODE_TYPES = new Set(['project', 'rfq', 'vendor', 'product', 'mcp', 'vendor_response']);

export function CostPlanGraphReagraph({ costPlanId, costPlanName }: CostPlanGraphReagraphProps) {
  const [isClient, setIsClient] = useState(false);
  const [GraphCanvas, setGraphCanvas] = useState<any>(null);
  const [themes, setThemes] = useState<{ dark: any; light: any } | null>(null);
  const { resolvedTheme } = useTheme();
  const selectNode = useGraphSelectionStore((state) => state.selectNode);
  const graphRef = useRef<any>(null);

  const [selectedNodes, setSelectedNodes] = useState<string[]>([]);
  const [activeNodes, setActiveNodes] = useState<string[]>([]);

  const [pathFindingMode, setPathFindingMode] = useState(false);
  const [pathStart, setPathStart] = useState<string | null>(null);
  const [pathEnd, setPathEnd] = useState<string | null>(null);
  const [pathNodes, setPathNodes] = useState<string[]>([]);

  const [contextMenu, setContextMenu] = useState<{
    x: number;
    y: number;
    node: any;
  } | null>(null);

  const [clusteringEnabled, setClusteringEnabled] = useState(false);

  // Type filter — which node types to HIDE, not show; empty set means
  // everything visible (the default, matches today's unfiltered
  // behavior exactly). A real HOX plan is 220 nodes / 590 edges across
  // 9 types with no way to isolate one — the legend at the bottom
  // already enumerates every type with its color, so toggling it into
  // a filter reuses existing chrome instead of adding a new control.
  const [hiddenTypes, setHiddenTypes] = useState<Set<string>>(new Set());
  const toggleType = useCallback((type: string) => {
    setHiddenTypes((prev) => {
      const next = new Set(prev);
      if (next.has(type)) next.delete(type); else next.add(type);
      return next;
    });
  }, []);

  const mcpGraphStrategy = useCapabilityStore((s) => s.strategyName('cost_plans', 'graph'));
  const { data, isLoading, error } = useMcpGraph(mcpGraphStrategy!, costPlanId);

  // ObeyaCostPlanGraphCard's own useCardExport call only knows the id it
  // was handed — it never sees `data`, so it can't declare the query_key
  // that would let this card's actual content (the nodes/edges being
  // painted) land in a captured scene's `payloads`. The doctrine
  // (card-export.tsx's "what data does this card NEED to paint its
  // content?") says that belongs here, next to where `data` materializes,
  // not on the wrapper. query_key must match useMcpGraph's own key
  // exactly (hooks.ts) or getObservedPayloads' cache lookup misses.
  const { contentKey } = useCardIdentity();
  useCardExport(useMemo(() => {
    const nodeTypeCounts: Record<string, number> = {};
    for (const n of data?.nodes ?? []) {
      nodeTypeCounts[n.type] = (nodeTypeCounts[n.type] ?? 0) + 1;
    }
    const status: 'loading' | 'error' | 'loaded' = isLoading ? 'loading' : error ? 'error' : 'loaded';
    return {
      content_key: contentKey ?? 'obeya_costplan_graph',
      status,
      error: error ? String((error as Error)?.message ?? error) : undefined,
      query_key: ['mcp-graph', mcpGraphStrategy, costPlanId],
      text: `Cost plan graph: ${data?.nodes.length ?? 0} nodes, ${data?.edges.length ?? 0} edges`,
      json: data
        ? { node_count: data.nodes.length, edge_count: data.edges.length, node_types: nodeTypeCounts }
        : null,
    };
  }, [contentKey, data, isLoading, error, mcpGraphStrategy, costPlanId]));

  useEffect(() => {
    setIsClient(true);
    import('reagraph').then((mod) => {
      setGraphCanvas(() => mod.GraphCanvas);

      const customDarkTheme = {
        ...mod.darkTheme,
        canvas: { ...mod.darkTheme.canvas, background: '#121212' },
        node: { ...mod.darkTheme.node, activeFill: '#ffffff', inactiveOpacity: 0.3 },
        edge: { ...mod.darkTheme.edge, activeFill: '#ffffff', inactiveOpacity: 0.2 },
        cluster: {
          ...mod.darkTheme.cluster,
          stroke: '#ffffff22', fill: '#ffffff11',
          label: { stroke: '#ffffff', color: '#ffffff' },
        },
      };
      const customLightTheme = {
        ...mod.lightTheme,
        canvas: { ...mod.lightTheme.canvas, background: '#ffffff' },
        node: { ...mod.lightTheme.node, activeFill: '#000000', inactiveOpacity: 0.3 },
        edge: { ...mod.lightTheme.edge, activeFill: '#000000', inactiveOpacity: 0.2 },
        cluster: {
          ...mod.lightTheme.cluster,
          stroke: '#00000022', fill: '#00000011',
          label: { stroke: '#000000', color: '#000000' },
        },
      };
      setThemes({ dark: customDarkTheme, light: customLightTheme });
    });
  }, []);

  const { nodes, edges } = useMemo(() => {
    if (!data?.nodes) return { nodes: [], edges: [] };

    const nodes = data.nodes.map((node: APIGraphNode) => ({
      id: node.id,
      label: node.label,
      fill: NODE_COLORS[node.type] || '#8AB4F8',
      size: NODE_SIZES[node.type] || 6,
      cluster: node.type,
      data: { type: node.type, status: node.status, odoo_id: node.odoo_id, ...node.extra },
    }));

    const edges = data.edges.map((edge: APIGraphEdge) => ({
      id: edge.id,
      source: edge.source,
      target: edge.target,
      label: edge.label || edge.type?.replace(/_/g, ' ') || '',
      fill: EDGE_COLORS[edge.type || ''] || EDGE_COLORS.default,
      size: edge.type === 'HAS_LINE' ? 2 : 1,
    }));

    return { nodes, edges };
  }, [data]);

  // What actually renders — full nodes/edges stay available for path-
  // finding (adjacencyList below) so a path can still be found through
  // a type a user has temporarily hidden; only the canvas and the
  // on-screen counts reflect the filter.
  const { visibleNodes, visibleEdges } = useMemo(() => {
    if (hiddenTypes.size === 0) return { visibleNodes: nodes, visibleEdges: edges };
    const visibleNodes = nodes.filter((n: any) => !hiddenTypes.has(n.data?.type));
    const visibleIds = new Set(visibleNodes.map((n: any) => n.id));
    const visibleEdges = edges.filter((e: any) => visibleIds.has(e.source) && visibleIds.has(e.target));
    return { visibleNodes, visibleEdges };
  }, [nodes, edges, hiddenTypes]);

  const adjacencyList = useMemo(() => {
    const adj: Record<string, string[]> = {};
    edges.forEach((edge: any) => {
      if (!adj[edge.source]) adj[edge.source] = [];
      if (!adj[edge.target]) adj[edge.target] = [];
      adj[edge.source].push(edge.target);
      adj[edge.target].push(edge.source);
    });
    return adj;
  }, [edges]);

  const findPath = useCallback((start: string, end: string): string[] => {
    if (start === end) return [start];
    const visited = new Set<string>();
    const queue: { node: string; path: string[] }[] = [{ node: start, path: [start] }];
    while (queue.length > 0) {
      const { node, path } = queue.shift()!;
      if (node === end) return path;
      if (visited.has(node)) continue;
      visited.add(node);
      const neighbors = adjacencyList[node] || [];
      for (const neighbor of neighbors) {
        if (!visited.has(neighbor)) queue.push({ node: neighbor, path: [...path, neighbor] });
      }
    }
    return [];
  }, [adjacencyList]);

  useEffect(() => {
    if (pathStart && pathEnd) {
      const path = findPath(pathStart, pathEnd);
      setPathNodes(path);
      if (path.length > 0) setSelectedNodes(path);
    } else {
      setPathNodes([]);
    }
  }, [pathStart, pathEnd, findPath]);

  const handleNodeClick = useCallback((node: any) => {
    if (pathFindingMode) {
      if (!pathStart) {
        setPathStart(node.id);
        setSelectedNodes([node.id]);
      } else if (!pathEnd && node.id !== pathStart) {
        setPathEnd(node.id);
      } else {
        setPathStart(node.id);
        setPathEnd(null);
        setPathNodes([]);
        setSelectedNodes([node.id]);
      }
    } else {
      // A lineitem itself has no detail view (see the comment below),
      // but it isn't a dead end: if it's quoted, traverse the same
      // "quoted" edge costplan_graph.py projects and select the real
      // VendorResponse instead — the one thing worth showing for a
      // line that has no card of its own. Highlight both ends of that
      // hop, not just the clicked line, so the jump reads as "this is
      // what backs it," not a silent redirect to somewhere else.
      const quotedEdge = node.data?.type === 'lineitem'
        ? data?.edges?.find((e: APIGraphEdge) => e.source === node.id && e.type === 'quoted')
        : null;
      setSelectedNodes(quotedEdge ? [node.id, quotedEdge.target] : [node.id]);

      // Only push node types mode1 actually has a rail/canvas binding
      // for (project/rfq/vendor/product/mcp/vendor_response — see
      // mode1.py's selection_presets/selection_insights, and obeya's
      // vendor_response -> rfi_details wiring). `lineitem` and `room`
      // are synthesized, non-addressable nodes (see costplan_graph.py's
      // module docstring) with no selection entry of their own —
      // pushing them anyway made useViewportSelection().entityId
      // resolve to a LineItem/Room id while the rail's mcp_details card
      // was still fetching costplan_details, i.e. "CostPlan LI_... not
      // found". A quoted lineitem gets the traversal above instead of a
      // bare local highlight; an unquoted one still just highlights.
      const targetNode = quotedEdge
        ? nodes.find((n: any) => n.id === quotedEdge.target)
        : node;
      const targetType = quotedEdge ? 'vendor_response' : node.data?.type;
      if (targetNode && SELECTABLE_NODE_TYPES.has(targetType)) {
        selectNode({
          id: targetNode.id,
          label: targetNode.label || targetNode.id,
          type: targetType || 'unknown',
          status: targetNode.data?.status,
          properties: targetNode.data,
        });
      }
    }
  }, [pathFindingMode, pathStart, pathEnd, selectNode, data, nodes]);

  const handleNodePointerOver = useCallback((node: any) => setActiveNodes([node.id]), []);
  const handleNodePointerOut = useCallback(() => setActiveNodes([]), []);

  const handleNodeContextMenu = useCallback((node: any, event: any) => {
    event.preventDefault?.();
    setContextMenu({ x: event.clientX || event.x || 100, y: event.clientY || event.y || 100, node });
  }, []);

  const closeContextMenu = useCallback(() => setContextMenu(null), []);

  const handleViewDetails = useCallback(() => {
    // Same SELECTABLE_NODE_TYPES guard as handleNodeClick — see its comment.
    if (contextMenu?.node && SELECTABLE_NODE_TYPES.has(contextMenu.node.data?.type)) {
      selectNode({
        id: contextMenu.node.id,
        label: contextMenu.node.label || contextMenu.node.id,
        type: contextMenu.node.data?.type || 'unknown',
        status: contextMenu.node.data?.status,
        properties: contextMenu.node.data,
      });
    }
    closeContextMenu();
  }, [contextMenu, selectNode, closeContextMenu]);

  const handleCopyId = useCallback(() => {
    if (contextMenu?.node) navigator.clipboard.writeText(contextMenu.node.id);
    closeContextMenu();
  }, [contextMenu, closeContextMenu]);

  const handleStartPath = useCallback(() => {
    if (contextMenu?.node) {
      setPathFindingMode(true);
      setPathStart(contextMenu.node.id);
      setPathEnd(null);
      setPathNodes([]);
      setSelectedNodes([contextMenu.node.id]);
    }
    closeContextMenu();
  }, [contextMenu, closeContextMenu]);

  const handleZoomIn = useCallback(() => graphRef.current?.zoomIn?.(), []);
  const handleZoomOut = useCallback(() => graphRef.current?.zoomOut?.(), []);
  const handleFitView = useCallback(() => graphRef.current?.fitNodesInView?.(), []);

  const resetPathFinding = useCallback(() => {
    setPathFindingMode(false);
    setPathStart(null);
    setPathEnd(null);
    setPathNodes([]);
    setSelectedNodes([]);
  }, []);

  useEffect(() => {
    if (contextMenu) {
      const handleClick = () => closeContextMenu();
      document.addEventListener('click', handleClick);
      return () => document.removeEventListener('click', handleClick);
    }
  }, [contextMenu, closeContextMenu]);

  if (!isClient || isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
        <Loader2 className="w-12 h-12 animate-spin mb-4" />
        <p>Loading cost plan graph...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-destructive">
        <AlertCircle className="w-12 h-12 mb-4" />
        <p>Failed to load graph</p>
        <p className="text-sm text-muted-foreground mt-2">
          {error instanceof Error ? error.message : 'Unknown error'}
        </p>
      </div>
    );
  }

  if (!nodes.length) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
        <Network className="w-12 h-12 mb-4 opacity-50" />
        <p>No graph data available</p>
        <p className="text-sm mt-2">This cost plan has no lines yet</p>
      </div>
    );
  }

  if (!GraphCanvas) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
        <Loader2 className="w-12 h-12 animate-spin mb-4" />
        <p>Loading graph renderer...</p>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      <div className="flex items-center justify-between px-4 py-2 border-b border-panel-border bg-panel">
        <div className="flex items-center gap-2">
          <Network className="w-4 h-4 text-primary" />
          <span className="text-sm font-medium">{costPlanName || 'Cost Plan Graph'}</span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setClusteringEnabled(!clusteringEnabled)}
            className={`p-1.5 rounded text-xs transition-colors ${
              clusteringEnabled ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:bg-muted'
            }`}
            title="Toggle clustering"
          >
            Cluster
          </button>

          <button
            onClick={() => (pathFindingMode ? resetPathFinding() : setPathFindingMode(true))}
            className={`p-1.5 rounded transition-colors ${
              pathFindingMode ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:bg-muted'
            }`}
            title="Find path between nodes"
          >
            <Route className="w-4 h-4" />
          </button>

          <div className="flex items-center border-l border-panel-border pl-2 ml-1">
            <button onClick={handleZoomIn} className="p-1.5 rounded hover:bg-muted transition-colors" title="Zoom in">
              <ZoomIn className="w-4 h-4" />
            </button>
            <button onClick={handleZoomOut} className="p-1.5 rounded hover:bg-muted transition-colors" title="Zoom out">
              <ZoomOut className="w-4 h-4" />
            </button>
            <button onClick={handleFitView} className="p-1.5 rounded hover:bg-muted transition-colors" title="Fit to view">
              <Maximize2 className="w-4 h-4" />
            </button>
          </div>

          <div className="flex items-center gap-3 text-xs text-muted-foreground border-l border-panel-border pl-2 ml-1">
            <span>
              {hiddenTypes.size > 0 ? `${visibleNodes.length} / ` : ''}{data?.metadata.node_count} nodes
            </span>
            <span>
              {hiddenTypes.size > 0 ? `${visibleEdges.length} / ` : ''}{data?.metadata.edge_count} edges
            </span>
            {hiddenTypes.size > 0 && (
              <button
                onClick={() => setHiddenTypes(new Set())}
                className="text-primary hover:underline"
                title="Clear type filter"
              >
                Clear filter
              </button>
            )}
          </div>
        </div>
      </div>

      {pathFindingMode && (
        <div className="flex items-center gap-2 px-4 py-2 bg-primary/10 border-b border-primary/20 text-sm">
          <Route className="w-4 h-4 text-primary" />
          <span>
            {!pathStart && 'Click a node to set start point'}
            {pathStart && !pathEnd && 'Click another node to find path'}
            {pathStart && pathEnd && pathNodes.length > 0 && (
              <span className="text-primary font-medium">Path found: {pathNodes.length} nodes</span>
            )}
            {pathStart && pathEnd && pathNodes.length === 0 && (
              <span className="text-destructive">No path found</span>
            )}
          </span>
          <button onClick={resetPathFinding} className="ml-auto p-1 rounded hover:bg-primary/20 transition-colors" title="Exit path finding">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      <div className="flex-1 bg-background relative overflow-hidden" style={{ minHeight: '400px' }} suppressHydrationWarning>
        {visibleNodes.length === 0 ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-muted-foreground">
            <Network className="w-12 h-12 mb-4 opacity-50" />
            <p>Every node type is hidden</p>
            <button
              onClick={() => setHiddenTypes(new Set())}
              className="mt-2 text-sm text-primary hover:underline"
            >
              Clear filter
            </button>
          </div>
        ) : (
          <>
            <div className="absolute inset-0">
              <GraphCanvas
                ref={graphRef}
                nodes={visibleNodes}
                edges={visibleEdges}
                layoutType="forceDirected2d"
                clusterAttribute={clusteringEnabled ? 'cluster' : undefined}
                labelType="nodes"
                draggable
                animated
                cameraMode="pan"
                edgeArrowPosition="end"
                edgeLabelPosition="above"
                theme={themes ? (resolvedTheme === 'dark' ? themes.dark : themes.light) : undefined}
                selections={selectedNodes}
                actives={activeNodes}
                onNodeClick={handleNodeClick}
                onNodePointerOver={handleNodePointerOver}
                onNodePointerOut={handleNodePointerOut}
                onNodeContextMenu={handleNodeContextMenu}
              />
            </div>

            {contextMenu && (
              <div
                className="absolute z-50 bg-popover border border-border rounded-md shadow-lg py-1 min-w-[160px]"
                style={{ left: contextMenu.x, top: contextMenu.y }}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="px-3 py-1.5 text-xs text-muted-foreground border-b border-border mb-1">
                  {contextMenu.node.data?.type}: {contextMenu.node.label}
                </div>
                <button onClick={handleViewDetails} className="w-full flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-muted transition-colors">
                  <Eye className="w-4 h-4" />
                  View Details
                </button>
                <button onClick={handleStartPath} className="w-full flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-muted transition-colors">
                  <Route className="w-4 h-4" />
                  Find Path From Here
                </button>
                <button onClick={handleCopyId} className="w-full flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-muted transition-colors">
                  <Copy className="w-4 h-4" />
                  Copy Node ID
                </button>
                {contextMenu.node.data?.odoo_id && (
                  <button
                    onClick={() => {
                      window.open(`/odoo/${contextMenu.node.data.type}/${contextMenu.node.data.odoo_id}`, '_blank');
                      closeContextMenu();
                    }}
                    className="w-full flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-muted transition-colors"
                  >
                    <ExternalLink className="w-4 h-4" />
                    Open in Odoo
                  </button>
                )}
              </div>
            )}
          </>
        )}
      </div>

      <div className="flex items-center justify-between px-4 py-2 border-t border-panel-border bg-panel text-xs">
        <div className="flex items-center gap-3">
          {Object.entries(NODE_COLORS).map(([type, color]) => {
            const hidden = hiddenTypes.has(type);
            return (
              <button
                key={type}
                onClick={() => toggleType(type)}
                className={`flex items-center gap-1 px-1 py-0.5 rounded transition-colors hover:bg-muted ${
                  hidden ? 'opacity-40' : ''
                }`}
                title={hidden ? `Show ${type.replace(/_/g, ' ')} nodes` : `Hide ${type.replace(/_/g, ' ')} nodes`}
              >
                <div
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: color, opacity: hidden ? 0.3 : 1 }}
                />
                <span className={`text-muted-foreground capitalize ${hidden ? 'line-through' : ''}`}>
                  {type.replace(/_/g, ' ')}
                </span>
              </button>
            );
          })}
        </div>
        <div className="text-muted-foreground">
          <MousePointer2 className="w-3 h-3 inline mr-1" />
          Click legend to filter • Click node to select • Right-click for menu
        </div>
      </div>
    </div>
  );
}
